import React from 'react';
import {
  Button, Card, Badge, TextInput, Textarea, Select, BottomSheet, EmptyState, IconButton,
} from '@parsina/framework';
import {
  Sparkles, Trash2, Pencil, ChevronUp, ChevronDown, Stethoscope,
} from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import {
  DX_KINDS, DX_STATUSES, dxKindKey, dxStatusKey, originKey, likelihoodKey,
  dxStatusVariant, likelihoodVariant,
} from '../../constants/vocabulary.js';
import { isValidIcd } from '../../screens/Visit/Visit.shared.js';
import { CostChip } from '../common/ParsehCost.jsx';
import { AiAnswerNote } from '../common/PracticeNotice.jsx';

// تشخیص — what the student thinks is going on, and what the assistant offers to
// argue with.
//
// ── ICD is optional, shape-checked, and never looked up ──────────────────
//
// core.md §5.2 gives the regex and calls the column free text. This app ships no
// ICD table: a student practising is allowed to write a code that is wrong, and
// the only thing worth refusing is a value that is not a code at all («قلب»).
// The hint under the field says the shape rather than the rule.
//
// ── The differential writes NOTHING until a card is clicked ──────────────
//
// `encounterDifferential` returns a narrative plus a best-effort list. Each card
// carries «افزودن به تشخیص‌ها», and that click — not the answer arriving — is
// what creates a row, with `kind: differential` and `origin: ai` (core.md §3.4,
// §5.2). `origin` is immutable afterwards: a row that came from the model says so
// for the rest of its life, which is the whole provenance story.

const emptyForm = { title: '', icdCode: '', kind: 'primary', status: 'active', note: '' };

function DiagnosisForm({ value, onChange, onSubmit, onCancel, saving, testId }) {
  const { t } = useTranslation();
  const icdBad = !isValidIcd(value.icdCode);

  return (
    <Card style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }} data-testid={testId}>
      <TextInput
        label={t('visit.dx.title')}
        required
        value={value.title}
        onChange={(e) => onChange({ ...value, title: e.target.value })}
        data-testid="dx-title"
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <TextInput
          label={t('visit.dx.icd')}
          value={value.icdCode}
          onChange={(e) => onChange({ ...value, icdCode: e.target.value })}
          helperText={t('visit.dx.icdHint')}
          error={icdBad ? t('visit.dx.icdInvalid') : ''}
          dir="ltr"
          data-testid="dx-icd"
        />
        {/* The kit's Select renders `options`, never children (and its own
            placeholder row unless told not to). */}
        <Select
          label={t('visit.dx.kind')}
          value={value.kind}
          onChange={(e) => onChange({ ...value, kind: e.target.value })}
          options={DX_KINDS.map((k) => ({ value: k, label: t(dxKindKey(k)) }))}
          placeholder=""
          data-testid="dx-kind"
        />
        <Select
          label={t('visit.dx.status')}
          value={value.status}
          onChange={(e) => onChange({ ...value, status: e.target.value })}
          options={DX_STATUSES.map((st) => ({ value: st, label: t(dxStatusKey(st)) }))}
          placeholder=""
          data-testid="dx-status"
        />
      </div>
      <Textarea
        label={t('visit.dx.note')}
        rows={2}
        value={value.note}
        onChange={(e) => onChange({ ...value, note: e.target.value })}
        data-testid="dx-note"
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          size="sm"
          onClick={onSubmit}
          disabled={saving || !value.title.trim() || icdBad}
          data-testid="dx-submit"
        >
          {t('common.actions.save')}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>{t('common.actions.cancel')}</Button>
      </div>
    </Card>
  );
}

export default function DiagnosisPanel({
  diagnoses = [],
  readOnly = false,
  onAdd,
  onUpdate,
  onDelete,
  onReorder,
  onDifferential,
  differential,
  differentialCost,
  differentialBusy = false,
  onOpenDifferential,
}) {
  const { t } = useTranslation();
  const [adding, setAdding] = React.useState(false);
  const [form, setForm] = React.useState(emptyForm);
  const [editingId, setEditingId] = React.useState(null);
  const [editForm, setEditForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);

  const rows = [...diagnoses].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const submitNew = async () => {
    setSaving(true);
    try {
      await onAdd({
        title: form.title.trim(),
        icdCode: form.icdCode.trim() || null,
        kind: form.kind,
        status: form.status,
        note: form.note || null,
        origin: 'manual',
      });
      setForm(emptyForm);
      setAdding(false);
    } finally { setSaving(false); }
  };

  const submitEdit = async () => {
    setSaving(true);
    try {
      await onUpdate(editingId, {
        title: editForm.title.trim(),
        icdCode: editForm.icdCode.trim() || null,
        kind: editForm.kind,
        status: editForm.status,
        note: editForm.note || null,
      });
      setEditingId(null);
    } finally { setSaving(false); }
  };

  /** Move one row a place; the whole new order is persisted by the caller. */
  const move = (index, delta) => {
    const next = [...rows];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onReorder(next.map((r) => r.id));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="diagnosis-panel">
      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button size="sm" onClick={() => setAdding((v) => !v)} data-testid="dx-add">
            {t('visit.dx.add')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onDifferential}
            disabled={differentialBusy}
            data-testid="dx-differential"
          >
            <Sparkles size={14} aria-hidden style={{ marginInlineEnd: 6 }} />
            {differentialBusy ? t('visit.dx.differentialBusy') : t('visit.dx.differential')}
          </Button>
          <CostChip cost={differentialCost} before />
          {differential && (
            <Button size="sm" variant="ghost" onClick={onOpenDifferential} data-testid="dx-differential-open">
              {t('visit.dx.differentialLast')}
            </Button>
          )}
        </div>
      )}

      {adding && !readOnly && (
        <DiagnosisForm
          value={form}
          onChange={setForm}
          onSubmit={submitNew}
          onCancel={() => { setAdding(false); setForm(emptyForm); }}
          saving={saving}
          testId="dx-form"
        />
      )}

      {!rows.length && !adding && (
        <EmptyState
          icon={<Stethoscope size={44} strokeWidth={1.5} aria-hidden />}
          title={t('visit.dx.empty')}
          description={t('visit.dx.emptyBody')}
          data-testid="dx-empty"
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((row, i) => (
          editingId === row.id ? (
            <DiagnosisForm
              key={row.id}
              value={editForm}
              onChange={setEditForm}
              onSubmit={submitEdit}
              onCancel={() => setEditingId(null)}
              saving={saving}
              testId={`dx-edit-${row.id}`}
            />
          ) : (
            <Card key={row.id} style={{ padding: 10 }} data-testid={`dx-row-${row.id}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: C.textPrimary }}>{row.title}</span>
                {row.icdCode && (
                  <span dir="ltr" style={{ fontSize: 12, color: CT.mute, unicodeBidi: 'isolate' }}>
                    {row.icdCode}
                  </span>
                )}
                <Badge variant="outline">{t(dxKindKey(row.kind))}</Badge>
                <Badge variant={dxStatusVariant(row.status)}>
                  {t(dxStatusKey(row.status))}
                </Badge>
                {row.origin === 'ai' && (
                  <Badge variant="warning" data-testid={`dx-origin-${row.id}`}>
                    {t(originKey(row.origin))}
                  </Badge>
                )}
                {!readOnly && (
                  <span style={{ marginInlineStart: 'auto', display: 'flex', gap: 2 }}>
                    <IconButton
                      size={30}
                      icon={<ChevronUp size={15} aria-hidden />}
                      label={t('visit.dx.moveUp')}
                      onClick={() => move(i, -1)}
                      data-testid={`dx-up-${row.id}`}
                    />
                    <IconButton
                      size={30}
                      icon={<ChevronDown size={15} aria-hidden />}
                      label={t('visit.dx.moveDown')}
                      onClick={() => move(i, 1)}
                    />
                    <IconButton
                      size={30}
                      icon={<Pencil size={15} aria-hidden />}
                      label={t('common.actions.edit')}
                      onClick={() => {
                        setEditingId(row.id);
                        setEditForm({
                          title: row.title || '',
                          icdCode: row.icdCode || '',
                          kind: row.kind || 'primary',
                          status: row.status || 'active',
                          note: row.note || '',
                        });
                      }}
                      data-testid={`dx-edit-open-${row.id}`}
                    />
                    <IconButton
                      size={30}
                      variant="ghost"
                      icon={<Trash2 size={15} aria-hidden />}
                      label={t('common.actions.delete')}
                      onClick={() => onDelete(row.id)}
                      data-testid={`dx-delete-${row.id}`}
                    />
                  </span>
                )}
              </div>
              {row.note && (
                <p style={{ margin: '6px 0 0', fontSize: 12.5, lineHeight: 1.9, color: CT.ink2 }}>
                  {row.note}
                </p>
              )}
            </Card>
          )
        ))}
      </div>

    </div>
  );
}

/**
 * The row a differential card writes when it is clicked: `kind: differential`,
 * `origin: ai` — the provenance that is immutable afterwards (core.md §5.2).
 */
export const diagnosisFromDifferential = (item) => ({
  title: item.condition,
  icdCode: null,
  kind: 'differential',
  status: 'active',
  note: item.reasoning || null,
  origin: 'ai',
});

/**
 * The differential cards. The narrative leads; the parsed list may be empty (§8.5).
 *
 * Rendered by the SCREEN, not by the panel above: «دیدن کارت‌ها» in the
 * copilot column has to open it while the student is on the note tab, and a
 * sheet that lives inside a tab is a sheet that exists only while that tab is
 * mounted.
 */
export function DifferentialSheet({ open, onClose, result, onAdd, readOnly }) {
  const { t } = useTranslation();
  const [added, setAdded] = React.useState({});

  React.useEffect(() => { if (open) setAdded({}); }, [open, result]);

  if (!open) return null;
  const items = result?.items || [];

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('visit.dx.differentialTitle')}
      fullHeight
      maxWidth={720}
      closeLabel={t('common.actions.close')}
      data-testid="differential-sheet"
    >
      {result?.narrative && (
        <p style={{
          margin: '0 0 12px', fontSize: 13, lineHeight: 2, whiteSpace: 'pre-wrap', color: C.textPrimary,
        }}
        >
          {result.narrative}
        </p>
      )}
      {!items.length && (
        <p style={{ fontSize: 13, color: CT.mute }}>{t('visit.dx.differentialEmpty')}</p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((item, i) => {
          const likelihood = likelihoodKey(item.likelihood);
          return (
            <Card key={`${item.condition}-${i}`} style={{ padding: 12 }} data-testid={`differential-card-${i}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700 }}>{item.condition}</span>
                {/* An unknown likelihood is printed RAW rather than folded onto a
                    label this app chose — the mapper is partial on purpose. */}
                {item.likelihood && (
                  <Badge variant={likelihoodVariant(item.likelihood)}>
                    {likelihood ? t(likelihood) : item.likelihood}
                  </Badge>
                )}
              </div>
              {item.reasoning && (
                <p style={{ margin: '6px 0 0', fontSize: 12.5, lineHeight: 1.9, color: CT.ink2 }}>
                  {item.reasoning}
                </p>
              )}
              {!!(item.discriminators || []).length && (
                <p style={{ margin: '6px 0 0', fontSize: 12, color: CT.ink2 }}>
                  <strong>{t('visit.dx.discriminators')}: </strong>
                  {item.discriminators.join(' · ')}
                </p>
              )}
              {!!(item.nextSteps || []).length && (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: CT.ink2 }}>
                  <strong>{t('visit.dx.nextSteps')}: </strong>
                  {item.nextSteps.join(' · ')}
                </p>
              )}
              {!readOnly && (
                <Button
                  size="sm"
                  variant="outline"
                  style={{ marginTop: 10 }}
                  disabled={!!added[i]}
                  onClick={async () => { await onAdd(item); setAdded((p) => ({ ...p, [i]: true })); }}
                  data-testid={`differential-add-${i}`}
                >
                  {added[i] ? t('visit.dx.addedToList') : t('visit.dx.addToList')}
                </Button>
              )}
            </Card>
          );
        })}
      </div>
      <AiAnswerNote />
    </BottomSheet>
  );
}
